
import { Directive, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

type ButtonType = 'primary' | 'secondary' | 'tertiary' | '';

/**
 * A directive which changes the color, backgroud-color, box-shadow and border styles of the button element that it is
 * applied to. This directive is meant to be used for buttons whose base hue is not 500. For the standard buttons please
 * use the generated static styles.
 *
 * Sample usage:
 * ``` html
 * <button class="mpro-btn" color="blue" hue="300" mproButtton>Create</button>
 * <button class="mpro-btn" color="blue" hue="300" mproButtton="tertiary" [disabled]="true">Create</button>
 * ```
 */
@Directive({
  selector: '[mproButtton]'
})
export class ButtonDirective implements OnInit, OnChanges {
  @Input() mproButtton: ButtonType = 'primary';

  @Input() color?: string;
  @Input() hue?: string;

  @Input() @HostBinding('style.background-color') backgroundColor?: string;
  @Input() @HostBinding('style.border') border?: string;
  @Input() @HostBinding('style.border-color') borderColor?: string;
  @Input() @HostBinding('style.color') textColor?: string;
  @Input() @HostBinding('style.box-shadow') boxShadow?: string;
  @Input() @HostBinding('disabled') disabled?: boolean | string;
  @Input() @HostBinding('selected') selected?: boolean;


  @Output() public buttonClick = new EventEmitter<any>();


  public disableMouseEventStyleChanges: boolean = false;

  public dark: boolean = false;

  public ngOnInit(): void {
    if (this.mproButtton === '') {
      this.mproButtton = 'primary';
    }
    this.reset();
    if (this.mproButtton === 'tertiary') {
      this.backgroundColor = 'transparent';
      this.borderColor = 'transparent';
    }

    if (this.selected) {
      this.makeDarker();
    }
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.selected) {
      if (this.selected) {
        this.makeDarker(true);
      } else {
        this.reset();
      }
    }
  }

  @HostListener('mouseenter') mouseenter(): void {
    if (this.disableMouseEventStyleChanges) {
      return;
    }
    this.makeLighter();
  }

  @HostListener('mouseleave') mouseleave(): void {
    if (this.disableMouseEventStyleChanges) {
      return;
    }
    this.reset();
  }

  @HostListener('mousedown') mousedown(): void {
    if (this.disableMouseEventStyleChanges) {
      return;
    }
    this.makeDarker();
  }

  @HostListener('mouseup') mouseup(): void {
    if (this.disableMouseEventStyleChanges) {
      return;
    }
    this.makeLighter();
  }

  @HostListener('focus') focus(): void {
    if (this.disableMouseEventStyleChanges || this.mproButtton === 'tertiary' || this.isDisabled()) {
      return;
    }
    this.boxShadow = '0 0 4px ' + this.backgroundColor;
  }

  @HostListener('blur') blur() {
    if (this.disableMouseEventStyleChanges || this.mproButtton === 'tertiary' || this.isDisabled()) {
      return;
    }
    this.boxShadow = undefined;
  }

  @HostListener('click', ['$event'])
  public onClick(ev: MouseEvent) {
    this.buttonClick.emit(ev);
  }

  public reset(): void {
    if (this.isDisabled()) {
      let gray100 = this.getColor("gray", 100);
      let gray400 = this.getColor("gray", 400);
      switch (this.mproButtton) {
        case 'primary':
          this.backgroundColor = gray400;
          this.textColor = "#ffffff";
          break;
        case 'secondary':
          this.border = '1px solid';
          this.borderColor = gray400;
          this.backgroundColor = gray100;
          this.textColor = gray400;
          break;
        case 'tertiary':
          this.textColor = gray400;
          break;
      }
      return;
    }

    if (!this.color || !this.hue) {
      return;
    }

    let hueInt = parseInt(this.hue);
    let color = this.getColor(this.color, hueInt);
    switch (this.mproButtton) {
      case 'primary':
        this.backgroundColor = color;
        this.textColor = hueInt > 300 ? "#ffffff" : "#000000";
        break;
      case 'secondary':
        this.border = '1px solid';
        this.borderColor = color;
        this.backgroundColor = this.dark ? colorMap['blue']['800'] : "#ffffff";
        this.textColor = this.dark ? "#ffffff" : color;
        break;
      case 'tertiary':
        this.textColor = color;
        break;
    }
  }

  public makeLighter(): void {
    this.changeHue(true, hue => hue <= 100);
  }

  public makeDarker(noBoundaryCheck = false): void {
    this.changeHue(false, hue => hue >= 800 && !noBoundaryCheck)
  }

  private changeHue(lighter: boolean, hueBoundaryCheck: (hue: number) => boolean): void {
    if (this.isDisabled() || !this.color || !this.hue) {
      return;
    }

    let hueInt = parseInt(this.hue);
    if (hueBoundaryCheck(hueInt)) {
      return;
    }

    hueInt = lighter ? hueInt - 100 : hueInt + (this.disableMouseEventStyleChanges ? 0 : 100);
    let color = this.getColor(this.color, hueInt);
    switch (this.mproButtton) {
      case 'primary':
      case 'secondary':
        this.backgroundColor = color;
        this.textColor = hueInt > 300 && lighter ? "#ffffff" : "#000000";
        if (this.dark) {
          this.borderColor = this.backgroundColor;
        }
        break;
      case 'tertiary':
        this.textColor = color;
        break;
    }
  }

  private isDisabled(): boolean {
    // if disabled is added as an attribute without a value, this comes as an empty string
    return !!this.disabled || this.disabled === '';
  }

  private getColor(color: string, hue: number): string {
    return colorMap[color][hue]
  }
}
const colorMap: { [key: string]: { [k: string]: string } } = {
  "blue": {
    100: "#EEF4FF",
    200: "#E3EBFF",
    300: "#CEDCFE",
    400: "#B5CBFF",
    500: "#8AABFF",
    600: "#375EC0",
    700: "#29458C",
    800: "#243647"
  },
  "green": {
    100: "#EBFFF4",
    200: "#A7D9CB",
    300: "#A0C3B0",
    400: "#5FB887",
    500: "#28B869",
    600: "#458461",
    700: "#186B3E",
    800: "#0C3820"
  },
  "teal": {
    100: "#DAF0EC",
    200: "#ACE3DA",
    300: "#63BFB0",
    400: "#329988",
    500: "#0D806C",
    600: "#146356",
    700: "#0B483E",
    800: "#042E27"
  },
  "apricot": {
    100: "#FFEDCC",
    200: "#FFE4B3",
    300: "#F0CF90",
    400: "#FFC966",
    500: "#EDB347",
    600: "#ED9A00",
    700: "#A16900",
    800: "#6E4700"
  },
  "red": {
    100: "#FFE6E0",
    200: "#FFBBAD",
    300: "#FFA594",
    400: "#E89492",
    500: "#D78987",
    600: "#CF4644",
    700: "#9C3533",
    800: "#692423"
  },
  "gray": {
    0: "#ffffff",
    50: "#F7F7F7",
    100: "#E4E4E4",
    200: "#C9C9C9",
    300: "#ADADAD",
    400: "#929292",
    500: "#5C5C5C",
    600: "#454545",
    700: "#383838",
    800: "#252525"
  }
};

